/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { StudyEnvironment, StudyTrial, ClinicalApp, StudyTableMode } from './types'
import { getEtmfFeature, getRcrFeature } from '../../../services/api/api.settings'
import { useServiceContext } from '../../../root'
import { useRcmTableData } from '../../../hooks/useRcmTableData'
import { RcmTable } from '../../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../../components/table/TableComponents'
import { UserRole } from '../../../types/access'
import { useNavigate } from 'react-router'

interface StudiesTableProps {
  mode: StudyTableMode
  userRole: UserRole
}

export const StudiesTable: React.FC<StudiesTableProps> = ({ mode, userRole }) => {
  const { t } = useTranslation()
  const { service } = useServiceContext()
  const tableData = useRcmTableData()
  const [isEtmfEnabled, setIsEtmfEnabled] = useState(false)
  const [isRcrEnabled, setIsRcrEnabled] = useState(false)
  const navigate = useNavigate()

  // Check feature flags on mount
  useEffect(() => {
    Promise.all([getEtmfFeature(), getRcrFeature()]).then(([etmf, rcr]) => {
      setIsEtmfEnabled(!!etmf)
      setIsRcrEnabled(!!rcr)
    })
  }, [])

  // Role-specific feature flag logic
  const shouldRenderTable = useMemo(() => {
    // ETMF_SPECIALIST, RCR_USER, ETMF_SPONSOR: Show if ETMF OR RCR enabled
    if ([UserRole.ETMF_SPECIALIST, UserRole.RCR_USER, UserRole.ETMF_SPONSOR].includes(userRole)) {
      return isEtmfEnabled || isRcrEnabled
    }
    // ETMF_STUDY_USER, ETMF_MANAGER: Show only if ETMF enabled
    return isEtmfEnabled
  }, [userRole, isEtmfEnabled, isRcrEnabled])

  const translateClinicalApp = (app: string): string => {
    if ([ClinicalApp.ETMF, ClinicalApp.RACT, ClinicalApp.SIM].includes(app as ClinicalApp)) {
      return t(`CLINICAL_APPLICATION_${app}`)
    }
    return '—'
  }

  const handleStudyClick = (row: StudyEnvironment | StudyTrial) => {
    // ETMF_STUDY_USER with ETMF app: Go to staging area
    // All other roles/apps: Go to browser
    if (userRole === UserRole.ETMF_STUDY_USER && row.app === ClinicalApp.ETMF) {
      service.staging.setEnvironmentId(row.fsiId.toString())
      navigate(`/staging/stagingarea?environmentId=${row.fsiId}`)
    } else {
      // All other cases go to browser
      navigate(`/browser/${row.fsiId}`)
    }
  }

  if (!shouldRenderTable) {
    return null
  }

  return (
    <div className="etmf-overview studies-table">
      <RcmTable
        HeadRow={
          <TableHeadRow>
            <TableHeadCell sort="studyName">{t('TABLE_TH_STUDY_NAME')}</TableHeadCell>
            <TableHeadCell sort="protocolId">{t('TABLE_TH_PROTOCOL_ID')}</TableHeadCell>
            <TableHeadCell sort="app">{t('TABLE_TH_APPLICATION')}</TableHeadCell>
          </TableHeadRow>
        }
        Row={(props) => {
          const row = props.row as StudyEnvironment | StudyTrial
          const displayName =
            mode === 'environments' && 'envName' in row ? `${row.studyName} (${row.envName})` : row.studyName

          return (
            <TableRow {...props}>
              <td>
                <a className="cursor-pointer word-break" onClick={() => handleStudyClick(row)}>
                  {displayName}
                </a>
              </td>
              <td>
                <div className="word-break">{'protocolId' in row ? row.protocolId : '—'}</div>
              </td>
              <td>{translateClinicalApp(row.app)}</td>
            </TableRow>
          )
        }}
        className="chita-table"
        noResultsText="NO_RESULTS"
      />
    </div>
  )
}
